import { ApiError, authHeader } from '@/lib/api'

/**
 * Uploads one part, reporting byte-level progress.
 *
 * Uses XMLHttpRequest rather than fetch because fetch exposes no upload
 * progress events. With 8 MiB parts, per-part granularity would leave a small
 * file jumping straight from 0% to 100% and a large one advancing in visible
 * steps; byte-level progress makes both read as continuous movement.
 */
export function uploadPart(options: {
  uploadId: string
  partNumber: number
  blob: Blob
  signal: AbortSignal
  onProgress: (bytesSent: number) => void
}): Promise<{ etag: string }> {
  const { uploadId, partNumber, blob, signal, onProgress } = options

  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('PUT', `/api/uploads/${uploadId}/parts/${partNumber}`, true)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    // The server keeps no session, so every request must carry the token.
    for (const [name, value] of Object.entries(authHeader())) {
      xhr.setRequestHeader(name, value)
    }
    xhr.withCredentials = true

    const onAbort = () => xhr.abort()
    signal.addEventListener('abort', onAbort, { once: true })

    const cleanup = () => signal.removeEventListener('abort', onAbort)

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded)
    }

    xhr.onload = () => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as { etag: string })
        } catch {
          reject(new ApiError(xhr.status, 'BAD_RESPONSE', 'Malformed response from server'))
        }
        return
      }

      let code = 'ERROR'
      let message = `Part ${partNumber} failed (${xhr.status})`
      try {
        const body = JSON.parse(xhr.responseText) as {
          error?: { code?: string; message?: string }
        }
        if (body.error?.code) code = body.error.code
        if (body.error?.message) message = body.error.message
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      reject(new ApiError(xhr.status, code, message))
    }

    xhr.onerror = () => {
      cleanup()
      reject(new ApiError(0, 'NETWORK', 'Network error while uploading'))
    }

    xhr.onabort = () => {
      cleanup()
      reject(new DOMException('Aborted', 'AbortError'))
    }

    xhr.send(blob)
  })
}
